import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * The search field above a pricelist's tables. Purely presentational — where the
 * matching happens depends on the pricelist's size and is the caller's problem.
 */
export function PricelistSearchBox({
  value,
  onChange,
  isSearching,
}: {
  value: string;
  onChange: (value: string) => void;
  isSearching: boolean;
}) {
  const gt = useGT();
  const label = gt('Search this pricelist', {
    context: 'label and placeholder for the in-pricelist table search field',
  });

  // A row, not an absolutely-positioned status: the panel version sits in a tight
  // column where anything overflowing the field lands on the tab strip below it.
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="relative min-w-0 flex-1 sm:max-w-sm">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="pr-8 pl-8"
          placeholder={label}
          aria-label={label}
        />
        {value !== '' ? (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-1/2 right-0 size-8 -translate-y-1/2"
            onClick={() => onChange('')}
            aria-label={gt('Clear search', {
              context:
                'accessible label for clearing the pricelist search field',
            })}
          >
            <XIcon className="size-4" />
          </Button>
        ) : null}
      </div>
      {isSearching ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          <T context="status shown while a pricelist search is running">
            Searching…
          </T>
        </span>
      ) : null}
    </div>
  );
}
